import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const sections = [{
  number: '01',
  heading: 'The Entropy Audit of Foreign Partners',
  paragraphs: ['Before any trade agreement is approved, the Sovereign Tier conducts a National Entropy Audit of the prospective partner state.', "This audit measures factors such as the population's susceptibility to outrage, the consistency of leadership judgment, the prevalence of synthetic noise in the media environment, and the degree to which decision-making is distorted by propaganda or short-termism.", 'If a partner is deemed high-entropy, the Cognarchist State imposes a Cognitive Tariff. This is not a financial tax in the conventional sense, but a requirement for heightened transparency, stricter verification, and logic-gated smart contracts designed to prevent external instability from contaminating the Cognarchist economy.']
}, {
  number: '02',
  heading: 'The Buffer Layer of Trade Intermediaries',
  paragraphs: ['Cognarchist citizens do not engage directly with captured or cognitively unstable foreign systems. All international commerce passes through a Sovereign Tier Interface.', 'This interface functions as a kind of economic limbic firewall. It strips away manipulative branding, emotional triggers, and theatrical marketing, reducing foreign inputs to their essential terms: utility, cost, reliability, and long-term systemic consequence.', 'Its purpose is simple: to prevent the culture of spectacle and emotional manipulation from leaking into a civilization organized around self-mastery, clarity, and disciplined judgment.']
}, {
  number: '03',
  heading: 'The Export of Cognitive Standards',
  paragraphs: ['The Cognarchist State treats the NCJI Protocol as one of its most valuable exports.', 'Foreign companies, regions, or institutions that voluntarily adopt NCJI-style verification for leadership and decision-making gain Preferred Signal Status. This grants them lower-friction trade access, greater institutional trust, and eligibility for investment through the Merit-Credit (M-Cred) system.', 'Over time, this creates a powerful incentive structure: the world becomes more competitive not by shouting louder, but by demonstrating better judgment. In this way, Cognarchism does not merely protect itself from global disorder; it gradually pressures the outside world to recalibrate around competence.']
}, {
  number: '04',
  heading: 'The Sovereign Embargo',
  paragraphs: ['If a foreign state launches a deliberate cognitive strike—such as coordinated disinformation, destabilization campaigns, or limbically manipulative digital warfare—the Sovereign Override initiates a full Signal Embargo.', 'Under these conditions, digital, financial, and informational links are severed. The Cognarchy enters Autonomy Mode, relying on internal high-fidelity markets and Sovereign Tier defense systems until the external threat is neutralized, contained, or converted into a usable defensive countermeasure.']
}];
const outcomes = ['It rewards foreign reliability.', 'It filters foreign noise.', 'And it builds prosperity by protecting the integrity of the civilization within.'];
export function SovereignTrade() {
  return <article data-id="element-666">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-667">
        <div className="max-w-3xl mx-auto px-6" data-id="element-668">
          <FadeIn delay={0.1} direction="down" data-id="element-669">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-670">
              Resource
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-671">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-672">
              The Sovereign Trade Protocol
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-673">
            <div className="space-y-6 max-w-2xl" data-id="element-674">
              <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed" data-id="element-675">
                When a Cognarchist State engages with the old world—nations
                still governed by high-entropy democracy, captured
                authoritarianism, or short-term political instability—it must
                protect its signal integrity.
              </p>
              <p className="text-base md:text-lg text-cognarch-gray leading-relaxed" data-id="element-676">
                Under this model, trade is not merely an exchange of goods. It
                is also a channel through which information, incentives, and
                disorder can enter the system. The Sovereign Tier Protocol for
                Global Trade (STP-GT) exists to ensure that Cognarchism remains
                a closed-loop order of high-fidelity judgment while still
                participating in the global market.
              </p>
            </div>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4 mt-10" data-id="element-677">
            <Link to="/pillars" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-678">
              The Pillars of the Order
            </Link>
            <Link to="/capability" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-679">
              The Human Capability Engine
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-680">
          <div className="max-w-3xl mx-auto px-6" data-id="element-681">
            <FadeIn data-id="element-682">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-683">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-684">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-685">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-686">
              <div className="space-y-6" data-id="element-687">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-688">
                    {paragraph}
                  </p>)}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Prosperity Through Protection */}
      <section className="py-20 md:py-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-689">
        <div className="max-w-3xl mx-auto px-6" data-id="element-690">
          <FadeIn data-id="element-691">
            <div className="flex items-baseline gap-6 mb-8" data-id="element-692">
              <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-693">
                05
              </span>
              <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-694">
                Prosperity Through Protection
              </h2>
            </div>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-695">
            <div className="space-y-6" data-id="element-696">
              <p className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-697">
                The STP-GT ensures that the Cognarchist State remains a true
                Human Capability Utility. It engages the world not through the
                theatrics of traditional diplomacy, but through the disciplined
                synchronization of logic, competence, and trust.
              </p>

              <div className="border-t border-cognarch-border mt-8 pt-8 space-y-4" data-id="element-698">
                {outcomes.map((outcome, index) => <p key={index} className="text-cognarch-charcoal leading-[1.8] text-base md:text-lg font-serif" data-id="element-699">
                    {outcome}
                  </p>)}
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-700">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-701">
          <FadeIn data-id="element-702">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-703" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-704">
              We do not trade in shadows. We trade in signals. To partner with
              the Cognarchy is to commit to the discipline of truth.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-705" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-706">
              Signal integrity. Sovereign commerce. Disciplined exchange.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}